/**
 * Card Image Navigation — Click/arrow/dot navigation + hover slideshow
 * Max 8 images per card. Auto-cycles on hover if data-hover-slideshow="true".
 */
(function () {
  'use strict';

  /* ── H7: Respect prefers-reduced-motion ── */
  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ── Lazy-hydrate the stacked slideshow + lifestyle images ──
     Images 2-8 and the lifestyle overlay ship with data-src only (no src) so they stay
     off the initial page load even though opacity-0 keeps them inside the viewport on
     above-the-fold cards. We promote data-src -> src the first time the shopper engages
     the card (hover or keyboard focus), so a collection page loads one image per card
     instead of up to eight. Idempotent per container. */
  function hydrateCardImages(container) {
    if (container._imgsHydrated) return;
    container._imgsHydrated = true;
    var lazyImgs = container.querySelectorAll('img[data-src]');
    for (var i = 0; i < lazyImgs.length; i++) {
      var img = lazyImgs[i];
      if (img.dataset.srcset) {
        img.setAttribute('srcset', img.dataset.srcset);
        img.removeAttribute('data-srcset');
      }
      img.setAttribute('src', img.dataset.src);
      img.removeAttribute('data-src');
    }
  }

  /* ── H2: Single global visibilitychange listener instead of per-card ── */
  var activeSlideshows = [];
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) {
      activeSlideshows.forEach(function (stop) { stop(); });
    }
  });

  function initCardNav(container) {
    /* Prevent double-init after section re-render */
    if (container._cardNavInit) return;
    container._cardNavInit = true;

    var images = container.querySelectorAll('[data-card-image]');
    var dots = container.querySelectorAll('[data-card-dot]');
    var prevBtn = container.querySelector('[data-card-prev]');
    var nextBtn = container.querySelector('[data-card-next]');
    var total = images.length;

    if (total < 2) return;

    var current = 0;
    var slideshowTimer = null;
    var isHoverSlideshow = container.getAttribute('data-hover-slideshow') === 'true';
    var slideshowSpeed = parseInt(container.getAttribute('data-slideshow-speed'), 10) || 1200;

    function goTo(index) {
      if (index < 0) index = total - 1;
      if (index >= total) index = 0;

      images[current].classList.add('opacity-0');
      images[current].classList.remove('opacity-100');
      images[index].classList.remove('opacity-0');
      images[index].classList.add('opacity-100');

      dots.forEach(function (dot, i) {
        if (i === index) {
          dot.classList.add('bg-white', 'scale-125');
          dot.classList.remove('bg-white/50');
        } else {
          dot.classList.remove('bg-white', 'scale-125');
          dot.classList.add('bg-white/50');
        }
      });

      current = index;
    }

    function stopEvent(e) {
      e.preventDefault();
      e.stopPropagation();
    }

    /* Arrow navigation */
    if (prevBtn) {
      prevBtn.addEventListener('click', function (e) {
        stopEvent(e);
        stopSlideshow();
        goTo(current - 1);
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function (e) {
        stopEvent(e);
        stopSlideshow();
        goTo(current + 1);
      });
    }

    /* Dot navigation */
    dots.forEach(function (dot) {
      dot.addEventListener('click', function (e) {
        stopEvent(e);
        stopSlideshow();
        goTo(parseInt(dot.getAttribute('data-card-dot'), 10));
      });
    });

    /* Keyboard navigation */
    var card = container.closest('a, [data-card-images]');
    if (card) {
      card.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          stopSlideshow();
          goTo(current - 1);
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          stopSlideshow();
          goTo(current + 1);
        }
      });
    }

    /* ── Hover Slideshow ── */
    function startSlideshow() {
      /* H7: Skip auto-slideshow if user prefers reduced motion */
      if (!isHoverSlideshow || total < 2 || prefersReducedMotion.matches) return;
      stopSlideshow();
      slideshowTimer = setInterval(function () {
        goTo(current + 1);
      }, slideshowSpeed);
    }

    function stopSlideshow() {
      if (slideshowTimer) {
        clearInterval(slideshowTimer);
        slideshowTimer = null;
      }
    }

    function resetToFirst() {
      stopSlideshow();
      if (current !== 0) {
        goTo(0);
      }
    }

    if (isHoverSlideshow) {
      container.addEventListener('mouseenter', startSlideshow);
      container.addEventListener('mouseleave', resetToFirst);

      /* Pause slideshow when interacting with nav buttons */
      container.addEventListener('pointerdown', function () {
        stopSlideshow();
      });

      /* H2: Register this stop function for global visibilitychange cleanup */
      activeSlideshows.push(stopSlideshow);
    }

    /* ── Cleanup: clear interval when container is removed from DOM ── */
    var cleanupObserver = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var removed = mutations[i].removedNodes;
        for (var j = 0; j < removed.length; j++) {
          if (removed[j] === container || (removed[j].contains && removed[j].contains(container))) {
            stopSlideshow();
            cleanupObserver.disconnect();
            container._cardNavInit = false;
            /* H2: Remove from global registry */
            var idx = activeSlideshows.indexOf(stopSlideshow);
            if (idx > -1) activeSlideshows.splice(idx, 1);
            return;
          }
        }
      }
    });

    var observeTarget = container.closest('.shopify-section') || container.parentNode;
    if (observeTarget && observeTarget.parentNode) {
      cleanupObserver.observe(observeTarget.parentNode, { childList: true });
    }
  }

  function init() {
    var containers = document.querySelectorAll('[data-card-images]');
    containers.forEach(function (container) {
      /* Hydrate the deferred images on first engagement — attached independently of the
         slideshow so it still fires on single-image cards (which skip initCardNav). */
      var hydrate = function () { hydrateCardImages(container); };
      container.addEventListener('mouseenter', hydrate, { once: true });
      var link = container.closest('a');
      if (link) link.addEventListener('focusin', hydrate, { once: true });
      initCardNav(container);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Re-init on Shopify section render (Theme Editor / AJAX pagination) */
  document.addEventListener('shopify:section:load', init);
})();
